import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import SectionTitle from '../../components/ui/SectionTitle';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

interface Testimonial {
  id: number;
  name: string;
  location?: string;
  rating?: number;
  text: string;
  date: string;
  source: 'facebook' | 'google';
  isLocalGuide?: boolean;
  reviewCount?: number;
  photoCount?: number;
  priceRange?: string;
  mealType?: string;
  recommendedDishes?: string[];
  ratings?: {
    food?: number;
    service?: number;
    atmosphere?: number;
  };
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Mark Mabry',
    location: 'Local Guide',
    rating: 5,
    text: 'We love this place! This was our third time here in 2 trips to Reykjavik. The seafood stew is amazing, pizza was really good, and wood fired, and the Weinerschnitzel with the mushroom gravy was so good! We had great service and were able to relax with a good meal and nice bottle of wine. Highly recommend!',
    date: '2 months ago',
    source: 'google',
    isLocalGuide: true,
    reviewCount: 149,
    photoCount: 641,
    priceRange: 'kr 10,000–12,000',
    mealType: 'Dinner',
    ratings: {
      food: 5,
      service: 5,
      atmosphere: 5,
    },
  },
  {
    id: 2,
    name: 'Bill Cox',
    rating: 5,
    text: 'Excellent pizza. My first meal of the trip and it starts off excellent. Wood fired pizza came out quick and delicious. Three bottles on the table, olive oil, garlic infused olive oil, lemon color and burnt orange which was the spicy. MAKE THIS A STOP.',
    date: 'a month ago',
    source: 'google',
    reviewCount: 3,
    photoCount: 2,
    mealType: 'Lunch',
  },
  {
    id: 3,
    name: 'Juhi Agarwal',
    location: 'Local Guide',
    rating: 5,
    text: 'Highly recommend this place for pizza in Reykjavik. We tried the pizza with ham and pepperoni on it, and it was absolutely fantastic. Also ordered the seafood soup, which had an amazing smell of mussels, prawns, and scallops in it, with all of it cooked perfectly. The decor and service were also quite good. The restaurant is also in a very nice area to walk.',
    date: '5 months ago',
    source: 'google',
    isLocalGuide: true,
    reviewCount: 174,
    photoCount: 988,
    priceRange: 'kr 2,000–4,000',
    mealType: 'Brunch',
    recommendedDishes: ['Pepperoni Pizza'],
    ratings: {
      food: 5,
      service: 5,
      atmosphere: 5,
    },
  },
  {
    id: 4,
    name: 'Renee M Smith',
    text: 'We had the beef filet with mushrooms, it was fabulous 👌. Quiet little place, excellent food!',
    date: '8. maí 2023',
    source: 'facebook',
  },
  {
    id: 5,
    name: 'Patricia Keenan',
    text: 'Service and food excellent, the lamb was delicious',
    date: '29. janúar 2023',
    source: 'facebook',
  },
  {
    id: 6,
    name: 'Lou Ro',
    text: 'A wonderful restaurant that even do gluten free pizza! Wonderful food, happy, welcoming staff and a relaxing ambiance.',
    date: '29. desember 2022',
    source: 'facebook',
  },
  {
    id: 7,
    name: 'Auður Dagný Jónsdóttir',
    text: 'The food is delicious 😋 We had steak, penne pasta 🍝, spaghetti bolognaise and margarita pizza 🍕',
    date: '30. janúar 2022',
    source: 'facebook',
  },
];

const Testimonials = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const nextTestimonial = () => {
    setDirection(1);
    setCurrentIndex(prevIndex => (prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1));
  };

  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentIndex(prevIndex => (prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1));
  };

  const goToTestimonial = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  // Animation variants for smooth transitions
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95,
      rotateY: direction > 0 ? 15 : -15,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
      rotateY: 0,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95,
      rotateY: direction < 0 ? 15 : -15,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  return (
    <section className="py-20 bg-neutral-100">
      <div className="container-custom overflow-hidden">
        <SectionTitle title={t.home.testimonials.title} subtitle={t.home.testimonials.subtitle} />

        <div className="relative max-w-4xl mx-auto w-full min-h-[400px] md:min-h-[350px] flex items-center perspective-1000">
          {/* Navigation Buttons */}
          <div className="absolute top-1/2 left-4 md:-left-12 -translate-y-1/2 z-10">
            <motion.button
              onClick={prevTestimonial}
              className="bg-white p-3 rounded-full shadow-lg hover:bg-red-50 transition-all duration-200 touch-manipulation hover:shadow-xl backdrop-blur-sm"
              aria-label="Previous testimonial"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <ChevronLeft className="text-red-800 w-5 h-5" />
            </motion.button>
          </div>

          <div className="absolute top-1/2 right-4 md:-right-12 -translate-y-1/2 z-10">
            <motion.button
              onClick={nextTestimonial}
              className="bg-white p-3 rounded-full shadow-lg hover:bg-red-50 transition-all duration-200 touch-manipulation hover:shadow-xl backdrop-blur-sm"
              aria-label="Next testimonial"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <ChevronRight className="text-red-800 w-5 h-5" />
            </motion.button>
          </div>

          {/* Testimonial */}
          <div className="w-full px-4 md:px-16">
            <div className="relative overflow-hidden">
              <motion.div
                key={currentTestimonial.id}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.4 },
                  scale: { duration: 0.4 },
                  rotateY: { duration: 0.4 },
                }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipe = swipePower(offset.x, velocity.x);

                  if (swipe < -swipeConfidenceThreshold) {
                    nextTestimonial();
                  } else if (swipe > swipeConfidenceThreshold) {
                    prevTestimonial();
                  }
                }}
                className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8 w-full cursor-grab active:cursor-grabbing"
                whileHover={
                  window.innerWidth > 768
                    ? {
                        y: -5,
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        transition: { duration: 0.3 },
                      }
                    : {}
                }
              >
                <div className="space-y-3">
                  {/* Header with name, location, and source info */}
                  <motion.div
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <div>
                      <h3 className="font-serif text-lg sm:text-xl font-semibold text-neutral-900">
                        {currentTestimonial.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-neutral-600">
                        {currentTestimonial.location && <span>{currentTestimonial.location}</span>}
                        {currentTestimonial.isLocalGuide && (
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                            Local Guide
                          </span>
                        )}
                        {currentTestimonial.reviewCount && (
                          <span>{currentTestimonial.reviewCount} reviews</span>
                        )}
                        {currentTestimonial.photoCount && (
                          <span>{currentTestimonial.photoCount} photos</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {currentTestimonial.source === 'google' && (
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">Google</span>
                        )}
                        {currentTestimonial.source === 'facebook' && (
                          <span className="text-xs bg-blue-100 px-2 py-1 rounded">Facebook</span>
                        )}
                        <span className="text-xs text-neutral-500">{currentTestimonial.date}</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Rating stars (only if rating exists) */}
                  {currentTestimonial.rating && (
                    <motion.div
                      className="flex items-center gap-1"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{
                            delay: 0.4 + i * 0.1,
                            duration: 0.3,
                            type: 'spring',
                            stiffness: 500,
                            damping: 15,
                          }}
                        >
                          <Star
                            size={16}
                            className={`${
                              i < currentTestimonial.rating!
                                ? 'text-yellow-500 fill-yellow-500'
                                : 'text-gray-300'
                            }`}
                          />
                        </motion.div>
                      ))}
                    </motion.div>
                  )}

                  {/* Additional info for Google reviews */}
                  {currentTestimonial.source === 'google' && (
                    <motion.div
                      className="flex flex-wrap gap-4 text-sm text-neutral-600"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                    >
                      {currentTestimonial.mealType && <span>{currentTestimonial.mealType}</span>}
                      {currentTestimonial.priceRange && (
                        <span>{currentTestimonial.priceRange}</span>
                      )}
                    </motion.div>
                  )}

                  {/* Review text */}
                  <motion.blockquote
                    className="text-neutral-700 italic text-xs sm:text-sm md:text-base leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  >
                    "{currentTestimonial.text}"
                  </motion.blockquote>

                  {/* Detailed ratings for Google reviews */}
                  {currentTestimonial.ratings && (
                    <motion.div
                      className="flex flex-wrap gap-4 text-sm"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                    >
                      {currentTestimonial.ratings.food && (
                        <div className="flex items-center gap-1">
                          <span className="text-neutral-600">Food:</span>
                          <span className="font-medium">{currentTestimonial.ratings.food}/5</span>
                        </div>
                      )}
                      {currentTestimonial.ratings.service && (
                        <div className="flex items-center gap-1">
                          <span className="text-neutral-600">Service:</span>
                          <span className="font-medium">
                            {currentTestimonial.ratings.service}/5
                          </span>
                        </div>
                      )}
                      {currentTestimonial.ratings.atmosphere && (
                        <div className="flex items-center gap-1">
                          <span className="text-neutral-600">Atmosphere:</span>
                          <span className="font-medium">
                            {currentTestimonial.ratings.atmosphere}/5
                          </span>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Recommended dishes */}
                  {currentTestimonial.recommendedDishes &&
                    currentTestimonial.recommendedDishes.length > 0 && (
                      <motion.div
                        className="pt-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.5 }}
                      >
                        <p className="text-sm text-neutral-600 mb-2">Recommended dishes:</p>
                        <div className="flex flex-wrap gap-2">
                          {currentTestimonial.recommendedDishes.map((dish, index) => (
                            <motion.span
                              key={index}
                              className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{
                                delay: 0.8 + index * 0.1,
                                duration: 0.3,
                                type: 'spring',
                                stiffness: 400,
                                damping: 15,
                              }}
                            >
                              {dish}
                            </motion.span>
                          ))}
                        </div>
                      </motion.div>
                    )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Indicator Dots - Only visible on desktop */}
        <div className="hidden md:block">
          <motion.div
            className="flex justify-center space-x-2 mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-red-800' : 'bg-neutral-300'
                }`}
                aria-label={`View testimonial ${index + 1}`}
                whileHover={{ scale: 1.3 }}
                whileTap={{ scale: 0.9 }}
                animate={{
                  scale: index === currentIndex ? 1.2 : 1,
                  backgroundColor: index === currentIndex ? '#B5292C' : '#C5C3C4',
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              />
            ))}
          </motion.div>
        </div>

        {/* Review Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-8"
        >
          <div className="flex flex-col items-center justify-center gap-4">
            <p className="text-neutral-600 text-xs sm:text-sm">
              {t.home.testimonials.readMore.text}
            </p>
            <div className="flex gap-4 justify-center">
              <a
                href="https://www.facebook.com/rossopomodoro.is/reviews?locale=is_IS"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Read our Facebook reviews"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors text-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                {t.home.testimonials.readMore.facebook}
              </a>
              <a
                href="https://www.google.com/search?sca_esv=fc38d4cc441412ae&biw=1440&bih=731&sxsrf=AE3TifNTO6jOMOXVCCeRS8sYPCXEHakQFA:1750629197827&si=AMgyJEuzsz2NflaaWzrzdpjxXXRaJ2hfdMsbe_mSWso6src8sys9ZoeqMu-9hgOwmUVkAi5qceC-HFjXoAL-DMjF9z8PZvA-OQMLxPrmkQoU7LMLWjol1Uc94MBRu6ErXa2vlyIu5QlF&q=Rossopomodoro+Reviews&sa=X&ved=2ahUKEwiP_uD7gYaOAxXozgIHHU92AJ8Q0bkNegQIKxAE"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Read our Google reviews"
                className="inline-flex items-center gap-2 text-red-600 hover:text-red-800 font-medium transition-colors text-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t.home.testimonials.readMore.google}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
